// PROD-3183 (vitals part) — the ONE vitals capture an accepted "Dictated vitals" suggestion writes.
//
// THE MERGE IS THE POINT. Every vitals save (`CreateVitals` and `updateVitals` alike) deactivates the
// encounter's earlier capture and writes a new one (Scrips.Patient `VitalService.CreateUpdateVitalsAsync`).
// A body that carried only the dictated values would wipe every reading a nurse had entered. So the
// body is the encounter's CURRENT readings, carried as they are, with the accepted values laid over
// them and the new ones added.
//
// The panel decides which values are accepted: a value that already exists is KEPT by default, and
// is replaced only when the clinician chose the dictated one. This builder writes what it is given.
//
// Pure. Transport and cache live in `use-save-vitals.ts`.

import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';
import { computeBmi, makeReading, type VitalsRequestContext, type VitalsRequestResult } from './to-vitals-request';

/** One value to write: its code, the vitals card's display name and unit, and the value. */
export interface AcceptedVital {
  code: string;
  display: string;
  unit: string;
  value: string;
}

/**
 * What the host's accept resolves to: true once the server stored the capture, false when nothing
 * was written, 'recorded-vitals-changed' when a recorded reading for an accepted code changed since
 * the panel showed it (nothing was written; the clinician reviews the new value).
 */
export type DictatedVitalsAcceptResult = boolean | 'recorded-vitals-changed';

const PLAIN_DECIMAL = /^\d+(\.\d+)?$/;

const live = (items: readonly PatientVital[] | null | undefined) => (items ?? []).filter((v) => !v.isDeleted);

/**
 * The merged capture: every current reading, the accepted values over them, BMI recomputed when a
 * height or weight changed. `{ error }` when there is nothing to write or a value is not a number.
 */
export function buildDictatedVitalsRequest(
  accepted: readonly AcceptedVital[],
  ctx: VitalsRequestContext,
): VitalsRequestResult {
  if (accepted.length === 0) return { error: 'Nothing to add.' };
  const bad = accepted.find((a) => !PLAIN_DECIMAL.test(a.value.trim()));
  if (bad) return { error: `${bad.display} must be a number.` };

  const current = live(ctx.existing);
  const byCode = new Map<string, PatientVital>();
  for (const reading of current) {
    const code = reading.code?.code;
    if (code && !byCode.has(code)) byCode.set(code, reading);
  }

  const written = new Map<string, PatientVital>();
  const dropped = new Set<string>();
  for (const a of accepted) {
    written.set(a.code, makeReading({ loinc: a.code, display: a.display, unit: a.unit }, a.value.trim(), ctx));
  }

  const valueOf = (code: string) => written.get(code)?.quantity?.value ?? byCode.get(code)?.quantity?.value ?? '';
  if (written.has(VITAL_CODES.HEIGHT) || written.has(VITAL_CODES.WEIGHT)) {
    const bmi = computeBmi(valueOf(VITAL_CODES.HEIGHT), valueOf(VITAL_CODES.WEIGHT));
    if (bmi) {
      written.set(VITAL_CODES.BMI, makeReading({ loinc: VITAL_CODES.BMI, display: 'Body Mass Index', unit: 'kg/m2' }, bmi, ctx));
    } else {
      // A height or weight changed but BMI cannot be recomputed (for example a weight with no height
      // recorded). The old BMI belongs to the old values, so it is dropped, not carried.
      dropped.add(VITAL_CODES.BMI);
    }
  }

  // The current readings in their own order, each replaced where a value was accepted for its code,
  // then the codes the encounter had no reading for.
  const vitals: PatientVital[] = current
    .filter((reading) => !(reading.code?.code && dropped.has(reading.code.code)))
    .map((reading) => {
      const code = reading.code?.code;
      return code && written.has(code) && byCode.get(code) === reading ? (written.get(code) as PatientVital) : reading;
    });
  for (const [code, reading] of written) if (!byCode.has(code)) vitals.push(reading);

  const mode: 'create' | 'update' = current.some((v) => v.identifire) ? 'update' : 'create';
  return { mode, vitals };
}

const firstValueByCode = (items: readonly PatientVital[] | null | undefined) => {
  const map = new Map<string, string>();
  for (const reading of live(items)) {
    const code = reading.code?.code;
    if (code && !map.has(code)) map.set(code, reading.quantity?.value ?? '');
  }
  return map;
};

/**
 * True when a recorded reading for one of the accepted codes differs between what the panel showed
 * and a fresh read: someone saved vitals in between. The clinician chose "keep" or "use dictated"
 * against the old value, so nothing is written until they review the new one.
 */
export function recordedVitalsChanged(
  shown: readonly PatientVital[] | null | undefined,
  latest: readonly PatientVital[] | null | undefined,
  codes: readonly string[],
): boolean {
  const before = firstValueByCode(shown);
  const after = firstValueByCode(latest);
  return codes.some((code) => (before.get(code) ?? '') !== (after.get(code) ?? ''));
}
